/**
 * The one subscription to the `formTypes` collection. Consumers: the home page
 * tables, the artifact toolbars, the metrics settings, `ViewsTable` and
 * `VisibilitySettingsTab` — everything that needs form types by id or as a
 * label-sorted `Select` option list.
 * The listener error lands on a module-level proxy rather than on each caller's
 * `connect({ onError })`, because the singleton store keeps only the first
 * connector's callback and every later consumer would hold a dead channel and
 * read a failed listener as an empty collection.
 */
import { useEffect, useMemo } from 'react';
import { proxy, useSnapshot } from 'valtio';
import { FormType } from '#types/System.types';
import { createCollectionStore } from '../store';

const formTypesStore = createCollectionStore<FormType>('formTypes');

const formTypesListener = proxy<{ error: Error | null }>({ error: null });

export const useFormTypes = (): {
    data: FormType[];
    options: Array<{ value: string; label: string }>;
    loading: boolean;
    error: Error | null;
} => {
    useEffect(() => {
        formTypesStore.connect({ onError: (error) => { formTypesListener.error = error; } });
        return () => formTypesStore.disconnect();
    }, []);

    const snap = useSnapshot(formTypesStore.state);
    const listenerSnap = useSnapshot(formTypesListener);

    const data = useMemo<FormType[]>(() => {
        const result: FormType[] = [];
        for (const id of snap.docIds) {
            const entry = snap.docs.get(id);
            if (entry) result.push({ ...(entry.data as FormType), docId: id });
        }
        return result;
    }, [snap.docIds, snap.docs]);

    const options = useMemo(() => {
        return data
            .map((formType) => ({
                value: formType.docId ?? '',
                label: formType.description?.shortLabel || (formType.docId ?? ''),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [data]);

    return {
        data,
        options,
        loading: snap.loading,
        error: listenerSnap.error,
    };
};
